import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, ChevronLeft, ChevronRight, Loader, Rocket, X } from 'lucide-react';
import { modpackConfigsApiService } from '../../services/modpackConfigsApi';
import {
  CreateModpackConfigRequest,
  ModpackConfig,
  ModpackConfigEnv,
  ModpackConfigVocabularies,
} from '../../types/modpackConfig';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { ConfigFieldInput } from './ConfigFieldInput';
import { ProvisionDiscordPanel } from './ProvisionDiscordPanel';
import { AnnounceLaunchPanel } from './AnnounceLaunchPanel';
import { ProvisioningEntry } from './ProvisioningEntry';
import {
  CLUSTER_FIELDS,
  FieldSpec,
  IDENTITY_FIELDS,
  INDEX_FIELDS,
  NETWORK_FIELDS,
  applyPortConvention,
  validateConfigValues,
} from './configFields';
import { cn } from '../../utils/cn';

interface CreateModpackConfigModalProps {
  env: ModpackConfigEnv;
  vocabularies: ModpackConfigVocabularies | null;
  existing: ModpackConfig[];
  canAdmin: boolean;
  onClose: () => void;
  onSuccess: (address: string) => void;
}

const STEPS = [
  { id: 'identity', label: 'Pack', fields: IDENTITY_FIELDS },
  { id: 'network', label: 'Network', fields: NETWORK_FIELDS },
  { id: 'cluster', label: 'Cluster', fields: CLUSTER_FIELDS },
  { id: 'index', label: 'Catalogue', fields: INDEX_FIELDS.filter((f) => f.key !== 'date_added') },
] as const;

const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

/**
 * Launching a pack.
 *
 * Mr. Roboto asks for the same information across six Discord modals, where
 * nothing is visible until it has all been typed. Here it is four reviewable
 * steps over one form, so a mistake on the first page is still fixable on the
 * last.
 */
export const CreateModpackConfigModal = ({
  env, vocabularies, existing, canAdmin, onClose, onSuccess,
}: CreateModpackConfigModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [step, setStep] = useState(0);
  const [address, setAddress] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  // Off the public list until somebody shows it. A pack usually exists as a
  // row for a while before it is ready for players.
  const [hiddenUntilShown, setHiddenUntilShown] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set once the row exists. Provisioning happens after, and separately: it
  // talks to Discord and can fail on its own, and losing the row because the
  // forum could not be made would be the worse failure.
  const [created, setCreated] = useState<string | null>(null);
  const [provisionServers, setProvisionServers] = useState(false);

  useEffect(() => {
    setStep(0);
    setAddress('');
    setValues({});
    setError(null);
  }, []);

  const takenAddresses = useMemo(
    () => new Set(existing.map((c) => c.server_address.toLowerCase())),
    [existing],
  );

  const addressTaken = address.trim() !== '' && takenAddresses.has(address.trim().toLowerCase());
  const isReview = step === STEPS.length;

  // Typing the query port fills in the RCON port by convention, until the
  // user sets RCON by hand.
  const setValue = (key: string, value: string) =>
    setValues((prev) => applyPortConvention(prev, key, value));

  const validation = useMemo(() => validateConfigValues(values), [values]);
  const messageFor = (key: string) => ({
    error: validation.errors.find((m) => m.key === key)?.message,
    warning: validation.warnings.find((m) => m.key === key)?.message,
  });

  const canAdvance = step > 0 || (address.trim() !== '' && !addressTaken);

  const buildPayload = (): CreateModpackConfigRequest => {
    const payload: CreateModpackConfigRequest & Record<string, unknown> = {
      server_address: address.trim(),
    };
    for (const spec of [...IDENTITY_FIELDS, ...NETWORK_FIELDS, ...CLUSTER_FIELDS, ...INDEX_FIELDS]) {
      if (spec.key === 'date_added') continue; // always the server's clock
      if (spec.adminOnly && !canAdmin) continue;
      const raw = (values[String(spec.key)] ?? '').trim();
      if (raw === '') continue;
      payload[String(spec.key)] =
        spec.kind === 'number' || spec.kind === 'timestamp' || spec.kind === 'server_picker'
          ? Number(raw)
          : raw;
    }
    if (hiddenUntilShown) payload.hidden = 1;
    return payload;
  };

  const handleSubmit = async () => {
    setError(null);
    if (!address.trim()) {
      setError('A server address is required.');
      setStep(0);
      return;
    }
    if (validation.errors.length > 0) {
      setError(validation.errors.map((m) => m.message).join(' '));
      setStep(1); // the network step, where the only hard rule lives
      return;
    }
    setSubmitting(true);
    try {
      const result = await modpackConfigsApiService.createConfig(buildPayload(), env);
      setCreated(result.server_address);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create the config');
    } finally {
      setSubmitting(false);
    }
  };

  const visibleFields = (fields: readonly FieldSpec[]) =>
    fields.filter((f) => !f.adminOnly || canAdmin);

  return createPortal(
    <div className="modal-overlay z-[9999]">
      <div ref={modalRef} className="modal-panel max-w-4xl max-md:max-w-[95vw]" role="dialog" aria-modal="true">
        <div className="modal-header">
          <div className="min-w-0">
            <h2 className="modal-title">Launch a pack</h2>
            <p className="modal-subtitle">
              {env === 'development'
                ? 'Creating in servers_dev — this will not affect live servers.'
                : provisionServers ? 'Prepare one or more servers for staff approval.' : 'Create a pack config, with optional server setup.'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="modal-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="modal-body space-y-4">
          {!created && env === 'production' && <label className="flex items-start gap-3 rounded-xl border border-accent-400/20 bg-accent-500/5 p-4 cursor-pointer">
            <input type="checkbox" className="mt-1" checked={provisionServers} onChange={event => setProvisionServers(event.target.checked)} disabled={submitting} />
            <span className="text-sm text-white">Set up Minecraft servers<span className="block text-xs text-glass-400 mt-1">Choose machines, storage and resources for one or more regional copies.</span></span>
          </label>}
          {!created && provisionServers && <ProvisioningEntry defaults={{ pack: {
            modpack_name: values.modpack_name ?? '', modpack_full_name: values.modpack_full_name ?? '',
            cluster_name: values.cluster_name ?? '', target_version: values.modpack_version ?? '',
            ...(values.modloader ? { modloader: values.modloader } : {}),
            ...(values.author ? { author: values.author } : {}),
            ...(values.thumbnail_url ? { thumbnail_url: values.thumbnail_url } : {}),
            ...(values.cluster_parent ? { mode: 'existing', cluster_parent: Number(values.cluster_parent) } : {}),
          }, server: { server_address: address, region_name: values.region_name ?? '', amp_name: values.amp_name ?? '' } }} />}
          {created && (
            <>
              <div className="alert alert-success" role="status">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span>
                  <span className="font-mono">{created}</span> created.
                  {env === 'development'
                    ? ' Development rows share the live pack\'s Discord furniture, so there is nothing to provision.'
                    : ' Give it a Discord forum and announce it below — or do either later from the pack\'s page.'}
                </span>
              </div>
              {env === 'production' && canAdmin && (
                <>
                  <ProvisionDiscordPanel address={created} env={env} />
                  <AnnounceLaunchPanel
                    address={created}
                    env={env}
                    // A pack that joined an existing cluster can launch with
                    // its siblings named alongside it.
                    members={[
                      { server_address: created, region_name: values.region_name ?? null },
                      ...existing.filter((c) => values.cluster_parent !== undefined && values.cluster_parent !== ''
                        && c.cluster_parent === Number(values.cluster_parent)),
                    ]}
                    defaults={{
                      location: values.region_name ?? null,
                      version: values.modpack_version ?? null,
                      url: values.server_download_url ?? null,
                    }}
                  />
                </>
              )}
              {env === 'production' && !canAdmin && (
                <p className="text-xs text-glass-400">
                  Creating the Discord forum needs argus.modpackconfigs.admin. Ask an admin to
                  finish the setup from the pack's page.
                </p>
              )}
            </>
          )}

          {!created && !provisionServers && (
          <>
          <div className="tabs flex-wrap max-w-full">
            {STEPS.map((s, i) => (
              <button
                key={s.id}
                type="button"
                className="tab"
                data-active={step === i}
                onClick={() => setStep(i)}
              >
                {i + 1}. {s.label}
              </button>
            ))}
            <button type="button" className="tab" data-active={isReview} onClick={() => setStep(STEPS.length)}>
              Review
            </button>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 0 && (
            <div>
              <label htmlFor="new-server-address" className={LABEL_CLASS}>
                Server address
              </label>
              <input
                id="new-server-address"
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="atm10.us.playcdu.co"
                className={cn('glass-input w-full font-mono', addressTaken && 'border-red-500/40')}
                required
              />
              <p className="text-xs text-glass-500 mt-1">
                The key every other system looks this server up by. It cannot be changed afterwards —
                a rename means creating the new entry and deleting the old one.
              </p>
              {addressTaken && (
                <p className="text-xs text-red-300 mt-1">That address already exists.</p>
              )}
            </div>
          )}

          {!isReview && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {visibleFields(STEPS[step].fields).map((spec) => (
                <ConfigFieldInput
                  key={String(spec.key)}
                  spec={spec}
                  value={values[String(spec.key)] ?? ''}
                  onChange={(v) => setValue(String(spec.key), v)}
                  vocabularies={vocabularies}
                  existing={existing}
                  {...messageFor(String(spec.key))}
                />
              ))}
            </div>
          )}

          {isReview && (
            <div className="subcard p-4 space-y-3">
              <p className="text-sm text-glass-300">
                <span className="font-mono text-white">{address || '—'}</span> will be created in{' '}
                <strong>{env === 'development' ? 'servers_dev' : 'production'}</strong>.
              </p>
              <p className="text-xs text-glass-400">
                If you leave the cluster head blank, the pack heads its own cluster — the same thing
                Mr. Roboto does after creating one.
              </p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="form-checkbox mt-0.5"
                  checked={hiddenUntilShown}
                  onChange={(e) => setHiddenUntilShown(e.target.checked)}
                />
                <span className="text-sm text-glass-200">
                  Keep hidden from players for now
                  <span className="block text-xs text-glass-400">
                    Leaves the server out of the public configs API and the LFG list until you press
                    “Show to players” on its page — usually at launch. Staff and Mr. Roboto see it either way.
                  </span>
                </span>
              </label>
              <dl className="divide-y divide-glass-200/10">
                {Object.entries(buildPayload())
                  .filter(([key]) => key !== 'server_address' && key !== 'hidden')
                  .map(([key, value]) => (
                    <div key={key} className="flex items-start justify-between gap-4 py-2">
                      <dt className="text-xs text-glass-400">{key}</dt>
                      <dd className="text-xs text-white font-mono text-right break-all">{String(value)}</dd>
                    </div>
                  ))}
              </dl>
              {Object.keys(buildPayload()).length === 1 && (
                <p className="text-xs text-glass-500">
                  Nothing but the address is set. You can fill the rest in later from the pack's page.
                </p>
              )}
            </div>
          )}
          </>
          )}
        </div>

        {created || provisionServers ? null : (
        <div className="modal-footer">
          {step > 0 && (
            <button type="button" onClick={() => setStep(step - 1)} className="glass-button-secondary mr-auto">
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
          )}
          <button type="button" onClick={onClose} className="glass-button-secondary">
            Cancel
          </button>
          {!isReview ? (
            <button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={!canAdvance}
              className="btn-primary"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !address.trim() || addressTaken || validation.errors.length > 0}
              className="btn-primary"
              title={validation.errors.length > 0 ? validation.errors[0].message : undefined}
            >
              {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
              Create pack
            </button>
          )}
        </div>
        )}

        {created && (
          <div className="modal-footer">
            <button
              type="button"
              onClick={() => onSuccess(created)}
              className="btn-primary ml-auto"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};
